/**
 * A chave de acesso da NFC-e, lida do QR Code do cupom.
 *
 * *"Leitura do QR Code da NFC-e quando disponível, que dá dado estruturado sem
 * OCR e sem custo de IA — sempre tentar primeiro."* —
 * `Docs/05-Plano/Fase 8 - Nota fiscal e IA.md`.
 *
 * ## O que o QR dá, e o que ele NÃO dá
 *
 * O QR carrega uma URL de consulta da SEFAZ com um parâmetro `p`, e o primeiro
 * campo dele é a **chave de acesso de 44 dígitos**. Ela é dado estruturado de
 * verdade: identifica o estado, o mês de emissão, o **CNPJ do estabelecimento**,
 * o modelo, a série e o número da nota — tudo sem OCR e sem IA.
 *
 * **Ela não traz os itens.** Para chegar nos itens é preciso consultar a página
 * da SEFAZ do estado, e isso é uma integração por estado, com HTML que muda
 * quando eles quiserem. Este módulo para na chave de propósito: o que ele
 * entrega é certo, e o resto é outro trabalho, com outro risco.
 *
 * O ganho já é grande: **quem** e **quando** vêm de graça, e a chave é o que
 * permite buscar o resto depois — sem depender de a foto estar legível.
 *
 * ## O dígito verificador é conferido, e é o ponto
 *
 * Uma chave com um dígito trocado — leitura ruim do QR, cupom amassado — é
 * **sintaticamente perfeita**: 44 dígitos, campos plausíveis. Só o módulo 11
 * separa uma chave de verdade de uma sequência de números, e sem ele o app
 * gravaria uma nota que não existe e a buscaria para sempre.
 */

export type NfceKey = {
  /** Os 44 dígitos. */
  key: string
  /** Código IBGE da UF do emitente. 41 é o Paraná. */
  uf: number
  /** Ano e mês de emissão, como `YYYY-MM`. */
  referenceMonth: string
  /** CNPJ do estabelecimento, só dígitos. É quem identifica a loja. */
  cnpj: string
  /** 65 é NFC-e; 55 é NF-e. */
  model: number
  series: number
  number: number
}

/**
 * Extrai a chave de um QR Code de NFC-e, ou de uma chave digitada.
 *
 * Aceita as três formas em que ela chega: a **URL inteira** do QR, o **valor do
 * parâmetro `p`** (que é a chave seguida de `|` e mais campos), e os **44
 * dígitos** sozinhos — que é como alguém a digita do rodapé do cupom, muitas
 * vezes com espaços a cada quatro.
 *
 * `null` quando não há chave válida. **Inclui dígito verificador errado**: ver o
 * cabeçalho.
 */
export function parseNfceKey(raw: string): NfceKey | null {
  const chave = extrairChave(raw)
  if (chave === null) return null
  if (!checkDigitOk(chave)) return null

  const ano = Number(chave.slice(2, 4))
  const mes = chave.slice(4, 6)
  return {
    key: chave,
    uf: Number(chave.slice(0, 2)),
    // O ano vem com dois dígitos. **2000 + ano**, e não uma janela deslizante:
    // a NFC-e existe desde 2013 e a chave não tem como representar 1999.
    referenceMonth: `${String(2000 + ano)}-${mes}`,
    cnpj: chave.slice(6, 20),
    model: Number(chave.slice(20, 22)),
    series: Number(chave.slice(22, 25)),
    number: Number(chave.slice(25, 34)),
  }
}

/**
 * Os 44 dígitos, de onde quer que venham.
 *
 * **Não usa `URL`**: o conteúdo do QR nem sempre é uma URL bem formada, e um
 * `new URL()` que lança num cupom torto derrubaria a leitura inteira. Procurar a
 * sequência de 44 dígitos funciona nas três formas de uma vez.
 */
function extrairChave(raw: string): string | null {
  // Espaços somem: a chave digitada do rodapé costuma vir em grupos de quatro.
  const limpo = raw.replace(/\s+/g, '')

  // O `p=` do QR traz a chave como primeiro campo, separada por `|`. Fora dele,
  // a própria string pode ser a chave. Em ambos os casos, o que se procura é
  // uma corrida de exatamente 44 dígitos que não faça parte de uma maior.
  const achados = limpo.match(/(?<!\d)\d{44}(?!\d)/g)
  if (achados === null || achados.length === 0) return null
  // A primeira: no `p=` ela é o primeiro campo, e os outros são curtos.
  return achados[0] ?? null
}

/**
 * O dígito verificador, módulo 11 sobre os 43 primeiros.
 *
 * Pesos de 2 a 9, ciclando **da direita para a esquerda** — é a especificação da
 * SEFAZ, e inverter o sentido produz um algoritmo que valida um conjunto
 * diferente de chaves, todas erradas.
 *
 * Resto 0 ou 1 dá dígito **0**, e não 11 ou 10. É o caso que uma implementação
 * ingênua erra, e ele acontece em cerca de 18% das chaves.
 */
export function checkDigitOk(key: string): boolean {
  if (!/^\d{44}$/.test(key)) return false

  let soma = 0
  let peso = 2
  for (let i = 42; i >= 0; i -= 1) {
    soma += Number(key[i]) * peso
    peso = peso === 9 ? 2 : peso + 1
  }
  const resto = soma % 11
  const dv = resto === 0 || resto === 1 ? 0 : 11 - resto
  return dv === Number(key[43])
}

/** `'41260812345678000199'` → `'12.345.678/0001-99'`. Para mostrar na tela. */
export function formatCnpj(cnpj: string): string {
  if (!/^\d{14}$/.test(cnpj)) return cnpj
  return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`
}
